import { ref, computed, watch } from 'vue';
import { defineStore } from 'pinia';
import { useSpotStore } from '@/stores/SpotStore'
import { useUserStore } from '@/stores/UserStore'
import { useAlertStore } from '@/stores/AlertStore'
import { useOnsiteReportsStore } from '@/stores/OnsiteReportsStore'
import { useProForecastStore } from '@/stores/ProForecastStore'
import { useBulletinStore } from '@/stores/BulletinStore';
import useStorageSystem from '@/composables/useStorageSystem'

export let useDetailPanelStore = defineStore('detailPanelStore', () => {
  const spotStore = useSpotStore();
  const userStore = useUserStore();
  const alertStore = useAlertStore();
  const onsiteReportsStore = useOnsiteReportsStore();
  const proForecastStore = useProForecastStore();
  const bulletinStore = useBulletinStore();

  const savedContainerTabs = {
    'Archive': 'archive-data',
    'Current Conditions': 'wind-graph',
    'Operational Forecast': 'pro-forecast',
    'Model Table': 'forecast-table',
    'Tides': 'nearby-tides',
    'Spot Stats': 'spot-stats',
    'Onsite Report': 'onsite-report',
  }

  const spotProductIdToContainerType = {
    1: 'wind-graph',
    2: 'forecast-table',
    3: 'pro-forecast',
    4: 'onsite-report',
    5: 'nearby-tides',
    6: 'marine-forecast',
    7: 'archive-data',
    8: 'spot-stats',
    9: 'spot-info',
    10: 'advisories',
    11: 'gorge-windbow',
  }

  const defaultSpotInfo = {
    spotId: null,
    favSpotId: null,
    lat: null,
    lon: null,
  }

  const [spotInfo] = useStorageSystem(defaultSpotInfo, 'detailPanelSpotInfo', 'localStorage')
  const [containerSettings] = useStorageSystem(null, 'detailPanelContainerSettings', 'localStorage')

  const panelOpen = ref(false);
  const panelElement = ref(null);
  const containerType = ref('wind-graph');
  const contentTab = ref(1)
  const showContent = ref(false);
  const spotName = ref('');
  
  const spotId = computed(() => spotInfo.value.spotId)

  const requests = computed(() => {
    // if (error.value) return;

    let promises = []

    if (spotStore.hasSpotProduct(10)) promises.push(alertStore.getAdvisoryByLatLon(spotStore.lat, spotStore.lon))
    if (spotStore.hasSpotProduct(4)) promises.push(onsiteReportsStore.getOnsiteReports(spotStore.lat, spotStore.lon))
    if (spotStore.hasSpotProduct(6)) promises.push(bulletinStore.getBulletins(spotStore.lat, spotStore.lon))

    // Need to get proforecast on page load for the wind graph pro forecast model
    if (showProForecast.value && userStore.memberLevel >= 49)
      promises.push(proForecastStore.getProForecast(spotStore.proForecastIdIfExists))

    return promises
  })

  const showProForecast = computed( () => spotStore.hasSpotProduct(3) && proForecastStore.canShowProForecast )
  const showOnsiteReports = computed( () => spotStore.hasSpotProduct(4) && onsiteReportsStore.hasOnsiteReports )
  const showAdvisories = computed(() => spotStore.hasSpotProduct(10) && alertStore.hasAlerts)
  const showMarineForecasts = computed( () => spotStore.hasSpotProduct(6) && bulletinStore.hasBulletins )
  const showNearbyTides = computed(() => spotStore.hasSpotProduct(5))
  const showWindGraph = computed( () => spotStore.hasSpotProduct(1) && !spotStore.isArbitrarySpot && !spotStore.isNowcast )
  const showForecastTable = computed(() => spotStore.hasSpotProduct(2))
  const showArchiveData = computed(() => spotStore.hasSpotProduct(7))
  const showStatsData = computed(() => spotStore.hasSpotProduct(8))
  const showInfoData = computed(() => spotStore.hasSpotProduct(9))
  const showCurrentConditions = computed(() => true)
  const showGorgeWindbow = computed(() => spotStore.hasSpotProduct(11));
  const savedContainerType = computed(() => Object.entries(savedContainerTabs).find(([key, value]) => value === containerType.value)?.[0] || null);

  const open = (tab) => {
    panelOpen.value = true;

    if (tab) changeTab(tab);

  };
  
  const toggle = () => {
    panelOpen.value = !panelOpen.value;
    dispatchPanelChanged();
  };

  const changeTab = (tab) => {
    if (tab === 1 && !showWindGraph.value) tab = 2;
    
    const type = spotProductIdToContainerType[tab];
    containerType.value = type || 'wind-graph';

    contentTab.value = type ? tab : 1;
    
    dispatchPanelChanged()
  };
  
  const updateSpotId = (id) => (spotInfo.value.spotId = id);

  function close() {
    panelOpen.value = false;
    containerType.value = 'wind-graph';
    containerSettings.value = null;

    dispatchPanelChanged();
  }

  function setPanelElement(element) {
    panelElement.value = element;
  }

  async function loadSpot(spotData) {
    showContent.value = false;
    
    if (spotData.requestData) {
      const id = spotData.id;
      spotName.value = 'Loading...';

      if (!id) {
        const lat = spotData.lat;
        const lon = spotData.lon;

        await spotStore.getSpotDetailByLatLon(`${lat}:${lon}`)
      } else {
        await spotStore.getSpotDetailById(id)
      }

      spotName.value = spotStore.spot.name;
    } else {
      spotName.value = spotData && spotData.name ? spotData.name : '';
      await spotStore.setCurrentSpotWithData(spotData);
    }
    
    await Promise.allSettled(requests.value);

    checkTabAvailability();

    showContent.value = true;
  }

  async function loadSavedContainer(type, settings) {
    settings = 
      !settings 
        ? null
        : typeof settings === 'string'
        ? JSON.parse(settings)
        : settings; 

    if (!settings) return;

    spotName.value = 'Loading...';

    spotInfo.value.spotId =
      settings.spotId && settings.spotId !== ''
        ? settings.spotId
        : null;

    spotInfo.value.favSpotId =
      settings.favSpotId && settings.favSpotId !== ''
        ? settings.favSpotId
        : null;

    spotInfo.value.lat =
      settings.lat && settings.lat !== ''
        ? settings.lat
        : null;

    spotInfo.value.lon =
      settings.lon && settings.lon !== ''
        ? settings.lon
        : null;

    if (settings.tideSpotSearch && settings.tideSpotSearch !== '') {
      const [lat, lon] = settings.tideSpotSearch.split(':');
      spotInfo.value.lat = lat;
      spotInfo.value.lon = lon;
    }

    if (type === 'Operational Forecast') {
      if (settings.forecastId) {
        await proForecastStore.getProForecast(settings.forecastId)

        if (!settings.spotId) {
          const centerPoint = proForecastStore.proForecastMapCenterPoint;
          spotInfo.value.lat = centerPoint.lat();
          spotInfo.value.lon = centerPoint.lng();
        }
      }
    }

    if (spotInfo.value.spotId) {
      await spotStore.getSpotDetailById(spotInfo.value.spotId);
    } else if (spotInfo.value.favSpotId) {
      await spotStore.getSpotDetailByFavoriteId(spotInfo.value.favSpotId);
    } else {
      await spotStore.getSpotDetailByLatLon(`${spotInfo.value.lat}:${spotInfo.value.lon}`)
    }

    spotName.value = spotStore.spot.name;      

    containerType.value = savedContainerTabs[type] || 'wind-graph';

    if ( containerSettings.value && Object.prototype.hasOwnProperty.call( containerSettings.value, containerType.value ) ) {
      containerSettings.value[containerType.value] = settings
    } else {
      containerSettings.value = {
        ...containerSettings.value,
        [containerType.value]: settings
      }
    }
  
    panelOpen.value = true;
    showContent.value = true;
  }

  function dispatchPanelChanged() {
    window.$EventBus.dispatchEvent('detail-panel-changed', { isOpen: panelOpen.value, tab: contentTab.value })
  }

  function checkTabAvailability() {
    if (!panelOpen.value) return;
    
    if (
      (containerType.value === 'nearby-tides' && !showNearbyTides.value) ||
      (containerType.value === 'onsite-report' && !showOnsiteReports.value) ||
      (containerType.value === 'marine-forecast' && !showMarineForecasts.value) ||
      (containerType.value === 'pro-forecast' && !showProForecast.value) ||
      (containerType.value === 'forecast-table' && !showForecastTable.value) ||
      (containerType.value === 'archive-data' && !showArchiveData.value) ||
      (containerType.value === 'spot-stats' && !showStatsData.value) ||
      (containerType.value === 'spot-info' && !showInfoData.value) ||
      (containerType.value === 'advisories' && !showAdvisories.value) ||
      (containerType.value === 'gorge-windbow' && !showGorgeWindbow.value)
    ) {
      changeTab(1)
    }

    if (containerType.value === 'wind-graph' && !showWindGraph.value) {
      changeTab(2)
    }
  }

  return {
    containerSettings,
    containerType,
    contentTab,
    panelElement,
    panelOpen,
    savedContainerType,
    showAdvisories,
    showArchiveData,
    showContent,
    showCurrentConditions,
    showForecastTable,
    showGorgeWindbow,
    showInfoData,
    showMarineForecasts,
    showNearbyTides,
    showOnsiteReports,
    showProForecast,
    showStatsData,
    showWindGraph,
    spotId,
    spotInfo,
    changeTab,
    checkTabAvailability,
    close,
    loadSpot,
    loadSavedContainer,
    open,
    setPanelElement,
    toggle,
    updateSpotId
  }
})
